/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import { FormHelperText, Grid } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import BaseService from '../../../services/BaseService';
import {
  ArquivoContainerV2,
  DefaultSelectV2,
  DialogDefaultV2,
  InputV2,
  NumberInputV2,
  SearchSelectV2,
} from '../../../components/v2';
import {
  MOTIVO_SOLICITACAO_OPTIONS,
  TIPO_ITEM_LABELS,
} from './statusLabels';

const catalogKey = (item) =>
  item && item.tipoItem && item.id ? `${item.tipoItem}::${item.id}` : '';

const ItemFormulario = ({ open, selectedItem, catalogoItens, onClose, onSave }) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [produtoTamanhos, setProdutoTamanhos] = useState([]);
  const [tamanhosLoading, setTamanhosLoading] = useState(false);

  const options = useMemo(
    () =>
      (catalogoItens || []).map((item) => ({
        value: catalogKey(item),
        label: item.nome,
        chip: TIPO_ITEM_LABELS[item.tipoItem] || item.tipoItem,
      })),
    [catalogoItens]
  );

  const selectedCatalog = useMemo(() => {
    if (!values.catalogKey) return null;
    return (catalogoItens || []).find((item) => catalogKey(item) === values.catalogKey) || null;
  }, [catalogoItens, values.catalogKey]);

  const pedeTamanho = !!(
    selectedCatalog
    && selectedCatalog.tipoItem === 'PRODUTO'
    && selectedCatalog.possuiTamanho
  );

  const tamanhoOptions = useMemo(() => {
    if (!pedeTamanho) return [];
    const produtoId = String(selectedCatalog.id);
    return produtoTamanhos
      .filter((item) => item.ativo === true && String(item.produtoId) === produtoId && item.tamanho)
      .map((item) => ({
        id: item.tamanho,
        nome: item.tamanho,
      }));
  }, [pedeTamanho, produtoTamanhos, selectedCatalog]);

  useEffect(() => {
    if (!open) return;
    if (selectedItem) {
      setValues({
        ...selectedItem,
        catalogKey: catalogKey({
          tipoItem: selectedItem.tipoItem,
          id: selectedItem.idItem,
        }),
        arquivos: selectedItem.arquivos || [],
      });
    } else {
      setValues({
        quantidade: null,
        motivo: '',
        arquivos: [],
      });
    }
    setErrors({});
  }, [open, selectedItem]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setTamanhosLoading(true);
    BaseService.get({ url: 'produtos/tamanhos/options' })
      .then((response) => {
        const data = response?.data;
        if (active) setProdutoTamanhos(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setProdutoTamanhos([]);
      })
      .finally(() => {
        if (active) setTamanhosLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const handleClose = () => {
    setValues({});
    setErrors({});
    onClose();
  };

  const schema = useMemo(() => {
    const next = {
      catalogKey: { presence: { allowEmpty: false, message: 'é obrigatório' } },
      quantidade: {
        presence: { allowEmpty: false, message: 'é obrigatório' },
        numericality: {
          onlyInteger: true,
          greaterThan: 0,
          message: 'deve ser maior que 0',
        },
      },
      motivo: { presence: { allowEmpty: false, message: 'é obrigatório' } },
    };
    if (pedeTamanho) {
      next.tamanho = { presence: { allowEmpty: false, message: 'é obrigatório' } };
    }
    return next;
  }, [pedeTamanho]);

  const onConfirm = () => {
    const result = validate(values, schema) || {};
    if (values.motivo === 'TROCA' && !(values.arquivos || []).length) {
      result.arquivos = ['é obrigatório'];
    }
    if (pedeTamanho && !tamanhosLoading && !tamanhoOptions.length) {
      result.tamanho = ['não há tamanho cadastrado neste produto'];
    }
    setErrors(result);
    if (Object.keys(result).length) return;
    if (!selectedCatalog) return;
    onSave({
      id: values.id || uuidv4(),
      tipoItem: selectedCatalog.tipoItem,
      idItem: selectedCatalog.id,
      nomeItem: selectedCatalog.nome,
      quantidade: Number(values.quantidade),
      observacao: values.observacao || null,
      motivo: values.motivo,
      tamanho: pedeTamanho ? values.tamanho : null,
      arquivos: values.arquivos || [],
    });
    handleClose();
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={handleClose}
      handlerConfirm={onConfirm}
      title={values.id ? 'Editar item' : 'Adicionar item'}
      confirmMessage={values.id ? 'Salvar' : 'Incluir'}
      maxWidth="md"
      fullWidth
    >
      <Grid container spacing={1} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12 }}>
          <SearchSelectV2
            label="Item"
            isRequired
            error={!!(errors && errors.catalogKey)}
            options={options}
            value={values.catalogKey || ''}
            sublabelNumericOnly={false}
            onSelect={(key) => {
              if (!key) return;
              setValues({
                ...values,
                catalogKey: key,
                tamanho: null,
              });
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <NumberInputV2
            label="Quantidade"
            name="quantidade"
            isRequired
            prefix=""
            numericValue
            decimalScale={0}
            thousandSeparator=""
            error={!!(errors && errors.quantidade)}
            value={values.quantidade}
            onChange={(quantidade) => setValues({ ...values, quantidade })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <DefaultSelectV2
            label="Motivo da solicitação"
            isRequired
            error={!!(errors && errors.motivo)}
            options={MOTIVO_SOLICITACAO_OPTIONS}
            value={values.motivo || ''}
            onSelect={(event) => {
              const motivo = event.target.value;
              if (motivo !== 'TROCA' && errors.arquivos) {
                setErrors({ ...errors, arquivos: undefined });
              }
              setValues({ ...values, motivo });
            }}
          />
        </Grid>
        {pedeTamanho && (
          <Grid size={{ xs: 12, sm: 4 }}>
            <DefaultSelectV2
              label="Tamanho"
              isRequired
              showEmpty
              error={!!(errors && errors.tamanho)}
              helperText={
                errors && errors.tamanho && !tamanhoOptions.length
                  ? 'Este produto não tem tamanhos cadastrados.'
                  : undefined
              }
              options={tamanhoOptions}
              disabled={tamanhosLoading || !tamanhoOptions.length}
              value={values.tamanho || ''}
              onSelect={(event) =>
                setValues({ ...values, tamanho: event.target.value })
              }
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <InputV2
            label="Observação"
            name="observacao"
            placeholder="Contexto específico deste item (fornecedor, modelo, urgência)..."
            value={values.observacao || ''}
            multiline
            rows={4}
            onChange={(event) =>
              setValues({ ...values, observacao: event.target.value })
            }
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ArquivoContainerV2
            headerTitle={values.motivo === 'TROCA' ? 'Arquivos *' : 'Arquivos'}
            arquivos={values.arquivos || []}
            onSave={(arquivos) => {
              setValues({ ...values, arquivos });
              if (errors.arquivos && (arquivos || []).length) {
                setErrors({ ...errors, arquivos: undefined });
              }
            }}
          />
          {values.motivo === 'TROCA' && (
            <FormHelperText error={!!errors.arquivos}>
              Em troca, anexe ao menos um arquivo.
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    </DialogDefaultV2>
  );
};

ItemFormulario.propTypes = {
  open: PropTypes.bool,
  selectedItem: PropTypes.object,
  catalogoItens: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

ItemFormulario.defaultProps = {
  open: false,
  selectedItem: null,
  catalogoItens: [],
};

export default ItemFormulario;
